'use client';

import { useSplit } from '@/components/providers/split-provider';
import { useDynamicFavicon } from '@/hooks/use-dynamic-favicon';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useToast } from '@/hooks/use-toast';
import {
    startImpersonation as apiStartImpersonation,
    stopImpersonation as apiStopImpersonation,
    fetchCompanies,
    type Company,
} from '@/lib/api/impersonation';
import { FeatureFlag } from '@/lib/feature-flags';
import { getCurrentUser, type UserData } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface ImpersonationContextType {
    isImpersonating: boolean;
    originalUser: UserData | null;
    currentUser: UserData | null;
    companies: Company[];
    isLoadingCompanies: boolean;
    startImpersonation: (companyId: number) => Promise<void>;
    stopImpersonation: () => Promise<void>;
    canImpersonate: boolean;
    isLoading: boolean;
    isUserOwnCompany: (company: Company) => boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export function useImpersonation() {
    const context = useContext(ImpersonationContext);
    if (context === undefined) {
        throw new Error('useImpersonation must be used within an ImpersonationProvider');
    }
    return context;
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
    const [isImpersonating, setIsImpersonating] = useState(false);
    const [originalUser, setOriginalUser] = useState<UserData | null>(null);
    const [currentUser, setCurrentUser] = useState<UserData | null>(null);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isLoginAsEnabled = useFeatureFlag(FeatureFlag.LOGIN_AS);
    const queryClient = useQueryClient();
    const { setUserAttributes } = useSplit();
    const { toast } = useToast();

    // Dynamic favicon based on impersonation status
    useDynamicFavicon({ isImpersonating });

    // Debug logging for impersonation state changes (removed for production)

    // Check if user can impersonate (admin user from aleph.vc domain or companyId 7)
    const canImpersonate = Boolean(
        isLoginAsEnabled && currentUser && (currentUser.email.endsWith('@aleph.vc') || currentUser.companyId === '7'),
    );

    // Initialize user data
    useEffect(() => {
        async function initializeUser() {
            try {
                const userData = await getCurrentUser();
                if (userData) {
                    // Check if there's an active impersonation session
                    const impersonationData = sessionStorage.getItem('impersonationData');
                    if (impersonationData) {
                        try {
                            const { originalUser: storedOriginalUser, impersonatedUser } =
                                JSON.parse(impersonationData);

                            // Validate that the stored original user matches the current logged-in user
                            // This prevents stale impersonation data from old sessions
                            if (
                                storedOriginalUser?.id === userData.id &&
                                storedOriginalUser?.email === userData.email
                            ) {
                                // Valid impersonation session found, restoring
                                setOriginalUser(storedOriginalUser);
                                setCurrentUser(impersonatedUser);
                                setIsImpersonating(true);
                            } else {
                                // Invalid impersonation session detected, clearing stale data
                                // Clear stale impersonation data
                                sessionStorage.removeItem('impersonationData');
                                setOriginalUser(userData);
                                setCurrentUser(userData);
                                setIsImpersonating(false);
                            }
                        } catch (error) {
                            console.error('Error parsing impersonation data:', error);
                            // Clear corrupted data
                            sessionStorage.removeItem('impersonationData');
                            setOriginalUser(userData);
                            setCurrentUser(userData);
                            setIsImpersonating(false);
                        }
                    } else {
                        // No sessionStorage data — detect impersonation from JWT:
                        // If the user is an @aleph.vc user but their JWT companyId is not '7',
                        // they are in an impersonation session that wasn't tracked in sessionStorage
                        // (e.g. after logout/login, new tab, or browser restart).
                        const isAlephUser = userData.email.endsWith('@aleph.vc');
                        const isNonAlephCompany = userData.companyId !== '7';

                        if (isAlephUser && isNonAlephCompany) {
                            // The impersonated company's display name lives in the dedicated
                            // `custom:impersonatedCompanyName` Cognito attribute (set by the
                            // /api/admin/impersonate handler, cleared on return-to-Aleph).
                            // Fall back to companyName, then to the companyId, so the banner is
                            // never blank even if the attribute is missing on older sessions.
                            const resolvedCompanyName =
                                userData.impersonatedCompanyName ||
                                userData.companyName ||
                                `Company ${userData.companyId}`;

                            // Reconstruct the original user with Aleph's companyId/Name.
                            const originalUserData: UserData = {
                                ...userData,
                                companyId: '7',
                                companyName: 'Aleph',
                                impersonatedCompanyName: undefined,
                            };
                            const impersonatedUser: UserData = {
                                ...userData,
                                companyName: resolvedCompanyName,
                                isImpersonated: true,
                                originalUserId: userData.id,
                            };

                            setOriginalUser(originalUserData);
                            setCurrentUser(impersonatedUser);
                            setIsImpersonating(true);

                            // Re-persist to sessionStorage so subsequent page loads don't need to re-detect
                            sessionStorage.setItem(
                                'impersonationData',
                                JSON.stringify({
                                    originalUser: originalUserData,
                                    impersonatedUser,
                                }),
                            );
                        } else {
                            setOriginalUser(userData);
                            setCurrentUser(userData);
                            setIsImpersonating(false);
                        }
                    }
                }
            } catch (error) {
                console.error('Error initializing user data:', error);
            }
        }

        initializeUser();
    }, []);

    // Load companies list for impersonation
    const loadCompanies = async () => {
        if (!canImpersonate) return;

        setIsLoadingCompanies(true);
        try {
            const response = await fetchCompanies();
            if (response.success) {
                setCompanies(response.data);
            } else {
                console.error('Failed to fetch companies:', response);
                setCompanies([]);
            }
        } catch (error) {
            console.error('Error loading companies:', error);
            // Fall back to empty array on error
            setCompanies([]);
        } finally {
            setIsLoadingCompanies(false);
        }
    };

    useEffect(() => {
        if (canImpersonate) {
            loadCompanies();
        }
    }, [canImpersonate]);

    // Helper function to extract domain from email
    const getUserEmailDomain = (email: string): string => {
        try {
            return email.split('@')[1]?.toLowerCase() || '';
        } catch (error) {
            console.error('Error extracting domain from email:', error);
            return '';
        }
    };

    // Helper function to check if company domain matches user's email domain
    const isUserOwnCompany = (company: Company, userEmail: string): boolean => {
        const userDomain = getUserEmailDomain(userEmail);
        const companyDomain = company.primaryDomain?.toLowerCase() || '';
        return userDomain === companyDomain && userDomain !== '';
    };

    // Helper function to refresh all application data
    const refreshApplicationData = async (userData: UserData) => {
        try {
            // Invalidate all React Query caches to force fresh data fetching
            await queryClient.invalidateQueries();

            // Update Split.io user attributes for the new company context
            setUserAttributes({
                email: userData.email,
                firstName: userData.name?.split(' ')?.[0] || '',
                lastName: userData.name?.split(' ')?.[1] || '',
                companyId: userData.companyId || '',
                companyName: userData.companyName || '',
                userId: userData.id,
                isImpersonated: userData.isImpersonated || false,
                originalUserId: userData.originalUserId || '',
            });

            // Application data refreshed for company
        } catch (error) {
            console.error('Error refreshing application data:', error);
        }
    };

    const startImpersonation = async (companyId: number) => {
        if (!canImpersonate || !originalUser) {
            throw new Error('Impersonation not allowed');
        }

        setIsLoading(true);
        try {
            // Find the selected company
            const selectedCompany = companies.find(c => c.id === companyId);
            if (!selectedCompany) {
                throw new Error('Company not found');
            }

            // Check if the selected company's domain matches the user's email domain
            if (isUserOwnCompany(selectedCompany, originalUser.email)) {
                // If it's the user's own company domain, automatically go back to Aleph
                // Selected company matches user domain, returning to Aleph
                await stopImpersonation();
                return;
            }

            // Proceed with normal impersonation for different domains
            const response = await apiStartImpersonation(companyId);

            if (response.success) {
                const impersonatedUser: UserData = {
                    ...originalUser,
                    companyId: response.data.companyId,
                    companyName: response.data.companyName,
                    isImpersonated: true,
                    originalUserId: originalUser.id,
                };

                // Store impersonation state
                const impersonationData = {
                    originalUser,
                    impersonatedUser,
                };
                sessionStorage.setItem('impersonationData', JSON.stringify(impersonationData));

                setCurrentUser(impersonatedUser);
                setIsImpersonating(true);

                // Show success message for impersonation start
                toast({
                    title: 'Impersonation Started',
                    description: `Now impersonating ${impersonatedUser.companyName} (Company ID: ${impersonatedUser.companyId})`,
                    variant: 'default',
                });

                // Refresh all application data for the new company context
                await refreshApplicationData(impersonatedUser);
            } else {
                throw new Error('Failed to start impersonation');
            }
        } catch (error) {
            console.error('Error starting impersonation:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const stopImpersonation = async () => {
        if (!isImpersonating) return;
        setIsLoading(true);
        try {
            // Call the real API to stop impersonation (return to Aleph, companyId 7)
            const response = await apiStopImpersonation();

            if (response.success) {
                setCurrentUser(originalUser);
                setIsImpersonating(false);
                sessionStorage.removeItem('impersonationData');

                // Show success message with Aleph company details
                toast({
                    title: 'Welcome back to Aleph!',
                    description: `You have returned to Aleph (Company ID: ${originalUser?.companyId || '7'})`,
                    variant: 'default',
                });

                // Refresh all application data back to the original user's context
                await refreshApplicationData(originalUser);
            } else {
                throw new Error('Failed to stop impersonation');
            }
        } catch (error) {
            console.error('Error stopping impersonation:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const contextValue: ImpersonationContextType = {
        isImpersonating,
        originalUser,
        currentUser,
        companies,
        isLoadingCompanies,
        startImpersonation,
        stopImpersonation,
        canImpersonate,
        isLoading,
        isUserOwnCompany: (company: Company) => (originalUser ? isUserOwnCompany(company, originalUser.email) : false),
    };

    return <ImpersonationContext.Provider value={contextValue}>{children}</ImpersonationContext.Provider>;
}
